import { useLayoutEffect, useRef, type RefObject } from 'react';

export type CheckoutOutcome = 'completed' | 'failed' | 'expired' | 'cancelled';

const CONTRACT_VERSION = 1;
const TYPE_PREFIX = 'motorplan.checkout.';
const OUTCOMES: readonly CheckoutOutcome[] = ['completed', 'failed', 'expired', 'cancelled'];

interface Params {
    checkoutUrl: string | null;
    applicationId: string | null;
    frameRef: RefObject<HTMLIFrameElement | null>;
    onOutcome: (outcome: CheckoutOutcome) => void;
}

function readOutcome(data: unknown): { outcome: CheckoutOutcome; checkoutId: string } | null {
    if (typeof data !== 'object' || data === null) return null;
    const { type, version, checkoutId } = data as Record<string, unknown>;
    if (version !== CONTRACT_VERSION || typeof type !== 'string' || typeof checkoutId !== 'string') {
        return null;
    }
    if (!type.startsWith(TYPE_PREFIX)) return null;
    const outcome = type.slice(TYPE_PREFIX.length) as CheckoutOutcome;
    return OUTCOMES.includes(outcome) ? { outcome, checkoutId } : null;
}

/** Gated on origin, the frame window captured at registration, application id
 *  and version. What passes is a UI trigger, never proof of payment. */
export function useCheckoutFrameEvents({ checkoutUrl, applicationId, frameRef, onOutcome }: Params): void {
    const onOutcomeRef = useRef(onOutcome);
    onOutcomeRef.current = onOutcome;
    const deliveredRef = useRef(new Set<string>());

    // Layout, not passive: elemis posts once on load for an already-terminal
    // checkout, and a one-shot message missed is missed for the whole mount.
    useLayoutEffect(() => {
        if (checkoutUrl === null || applicationId === null) return;
        // Captured, not re-read per message: a listener answers only for the
        // frame that was mounted when it registered.
        const frameWindow = frameRef.current?.contentWindow ?? null;
        if (frameWindow === null) return;
        let expectedOrigin: string;
        try {
            expectedOrigin = new URL(checkoutUrl).origin;
        } catch {
            return;
        }

        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== expectedOrigin) return;
            if (event.source !== frameWindow) return;
            const parsed = readOutcome(event.data);
            if (parsed === null || parsed.checkoutId !== applicationId) return;

            const key = `${parsed.checkoutId}:${parsed.outcome}`;
            if (deliveredRef.current.has(key)) return;
            deliveredRef.current.add(key);
            onOutcomeRef.current(parsed.outcome);
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [checkoutUrl, applicationId, frameRef]);
}
